/**
 * Ancoraggio alle sezioni di una pagina lunga: salto all'arrivo e indirizzo che
 * segue lo scorrimento.
 *
 * Vive a parte perché il comportamento «vai a quella sezione» sembra banale e
 * non lo è. Tre cose lo fanno fallire in silenzio, e sono tutte presenti qui:
 *
 * 1. **Il contenuto arriva dopo.** Il salto nativo del browser avviene alla
 *    navigazione, quando le sezioni non esistono ancora perché i dati sono
 *    ancora in rete. Il salto va rifatto a mano, dopo.
 * 2. **L'intestazione è appiccicata in alto.** Uno scorrimento esatto mette il
 *    titolo della sezione proprio sotto la barra, e sembra di essere finiti nel
 *    posto sbagliato.
 * 3. **Le sezioni nascono trasparenti.** Sono animate all'ingresso nella
 *    finestra: saltando direttamente a metà pagina si arriva dove si voleva e
 *    non si vede nulla.
 */

/** Altezza dell'intestazione appiccicata (`Header.tsx`), per lasciarle spazio.
 *  Su schermo stretto l'intestazione è più bassa e il margine può essere minore. */
const HEADER_OFFSET_DESKTOP = 88;
const HEADER_OFFSET_MOBILE = 64;

function headerOffset(): number {
  try {
    return window.innerWidth < 768 ? HEADER_OFFSET_MOBILE : HEADER_OFFSET_DESKTOP;
  } catch {
    return HEADER_OFFSET_DESKTOP;
  }
}

/** Chi ha chiesto meno animazioni non deve subire uno scorrimento lungo. */
function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Frammento dell'indirizzo, senza `#` e decodificato.
 *
 *  La decodifica serve perché uno slug con accenti o spazi arriva percentuato:
 *  `#citt%C3%A0` non corrisponderebbe mai a un identificatore `città`. */
export function currentHash(): string {
  try {
    return decodeURIComponent(window.location.hash.replace(/^#/, ""));
  } catch {
    return window.location.hash.replace(/^#/, "");
  }
}

/**
 * Porta in vista la sezione con quell'identificatore.
 *
 * @param onBeforeScroll eseguito con l'elemento trovato, prima di muoversi:
 *   serve a renderlo visibile se un'animazione lo tiene ancora trasparente.
 * @returns `true` se la sezione esisteva. Un identificatore sconosciuto —
 *   un indirizzo vecchio, un refuso — non fa nulla e lascia la pagina in cima,
 *   che è meglio di un salto verso il nulla.
 */
export function scrollToSection(
  id: string,
  onBeforeScroll?: (el: HTMLElement) => void,
): boolean {
  if (!id) return false;
  let el: HTMLElement | null = null;
  try {
    // `getElementById` e non `querySelector('#'+id)`: uno slug che comincia con
    // una cifra o contiene un punto è un selettore non valido e solleverebbe.
    el = document.getElementById(id);
  } catch {
    return false;
  }
  if (!el) return false;

  onBeforeScroll?.(el);

  const smooth = !prefersReducedMotion();
  const vaiA = (target: HTMLElement) => {
    const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: smooth ? "smooth" : "auto" });
    return Math.max(0, top);
  };

  const atteso = vaiA(el);

  /* Riallineamento finché la pagina si assesta.
   *
   * Le immagini delle sezioni precedenti finiscono di caricarsi DOPO lo
   * scorrimento e allungano il documento: la posizione calcolata su una pagina
   * più corta non è più quella giusta, e la sezione scivola in basso. Sulla
   * terza collezione, su schermo stretto, lo scarto misurato era di 92 pixel —
   * abbastanza da lasciare il titolo mezzo fuori.
   *
   * Una sola correzione a tempo non basta: le immagini continuano ad arrivare
   * anche dopo. Si osservano le variazioni di altezza del documento e si
   * ricorregge a ogni assestamento, per una finestra limitata.
   *
   * Ci si ferma appena **chi legge tocca la pagina**: strappargli la vista
   * sotto le dita è peggio del disallineamento che si sta correggendo.
   */
  avviaRiallineamento(id, atteso);
  return true;
}

/** Per quanto si continua a inseguire l'assestamento del documento. */
const SETTLE_WINDOW_MS = 3000;
/** Sotto questo scarto la sezione è dove deve essere: correggere sarebbe uno
 *  scatto visibile senza guadagno. */
const TOLERANCE_PX = 24;

function avviaRiallineamento(id: string, previsto: number): void {
  if (typeof ResizeObserver === "undefined") return;

  let ultimoPrevisto = previsto;
  let vivo = true;

  const smetti = () => {
    if (!vivo) return;
    vivo = false;
    observer.disconnect();
    window.clearTimeout(scadenza);
    EVENTI_UTENTE.forEach((e) => window.removeEventListener(e, smetti));
  };

  const riallinea = () => {
    if (!vivo) return;
    const el = document.getElementById(id);
    if (!el) return smetti();
    // Uno scarto grande fra dove avevamo lasciato la pagina e dov'è adesso
    // significa che l'ha mossa qualcun altro: ci si fa da parte.
    if (Math.abs(window.scrollY - ultimoPrevisto) > 400) return smetti();

    const scarto = el.getBoundingClientRect().top - headerOffset();
    if (Math.abs(scarto) <= TOLERANCE_PX) return;

    const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerOffset());
    // Salto secco e non morbido: qui si sta correggendo di poche decine di
    // pixel, e un'animazione a ogni immagine caricata darebbe l'impressione di
    // una pagina che trema.
    window.scrollTo({ top, behavior: "auto" });
    ultimoPrevisto = top;
  };

  const observer = new ResizeObserver(riallinea);
  observer.observe(document.documentElement);
  const scadenza = window.setTimeout(smetti, SETTLE_WINDOW_MS);
  EVENTI_UTENTE.forEach((e) => window.addEventListener(e, smetti, { passive: true, once: true }));
}

/** Segnali che chi legge ha preso il controllo. `scroll` non è fra questi: lo
 *  emettiamo anche noi correggendo, e ci fermeremmo da soli. */
const EVENTI_UTENTE = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

/**
 * Tiene l'indirizzo allineato alla sezione che si sta guardando.
 *
 * **`replaceState` e non `pushState`**: aggiornando la cronologia a ogni
 * sezione attraversata, il tasto «indietro» diventerebbe un modo per risalire
 * la pagina una sezione per volta invece di tornare da dove si è arrivati. È il
 * comportamento che fa dire «il tasto indietro non funziona».
 *
 * @param ids identificatori delle sezioni, nell'ordine in cui compaiono.
 */
export interface SectionWatcher {
  /** Smette di osservare. Da chiamare quando la pagina si smonta. */
  stop(): void;
  /** Sospende l'aggiornamento per la durata di uno scorrimento programmato. */
  suspend(ms?: number): void;
}

const NOOP_WATCHER: SectionWatcher = { stop() {}, suspend() {} };

export function watchSections(ids: string[]): SectionWatcher {
  if (!ids.length || typeof IntersectionObserver === "undefined") return NOOP_WATCHER;

  const elements = ids
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el !== null);
  if (!elements.length) return NOOP_WATCHER;

  // Durante uno scorrimento programmato si attraversano tutte le sezioni
  // intermedie: senza questa pausa l'indirizzo lampeggerebbe fra i loro nomi
  // prima di fermarsi su quello giusto.
  let suspended = false;
  let resumeTimer: ReturnType<typeof setTimeout> | null = null;

  const observer = new IntersectionObserver(
    (entries) => {
      if (suspended) return;
      // La sezione che occupa più spazio nella finestra è quella che si sta
      // guardando. Prendere la prima che entra darebbe il nome di una sezione
      // appena affacciata dal basso mentre se ne sta leggendo un'altra.
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;

      const id = visible.target.id;
      if (!id || currentHash() === id) return;
      try {
        window.history.replaceState(null, "", `${window.location.pathname}#${id}`);
      } catch {
        /* alcuni browser limitano replaceState: l'indirizzo resta indietro,
           la pagina continua a funzionare */
      }
    },
    {
      // La banda centrale della finestra: una sezione conta quando è davvero
      // sotto gli occhi, non quando spunta di un pixel dal bordo.
      rootMargin: "-45% 0px -45% 0px",
      threshold: [0, 0.25, 0.5, 0.75, 1],
    },
  );

  elements.forEach((el) => observer.observe(el));

  return {
    stop() {
      observer.disconnect();
      if (resumeTimer) clearTimeout(resumeTimer);
    },
    suspend(ms = 900) {
      suspended = true;
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => { suspended = false; }, ms);
    },
  };
}
